import { addBreadcrumb, reportError } from '@/utils/sentry';

/**
 * Diagnostics for dynamic-import / lazy-chunk load failures.
 *
 * Symptom we are chasing (seen in Sentry):
 *   Navigation -> /<locale>/itinerary
 *   SyntaxError: Unexpected end of input   (a lazy chunk failed to PARSE)
 *   ...surfacing through React.lazy/Suspense as `Error: undefined`
 *
 * The main bundle parses fine (the app runs), so only a runtime-served lazy
 * chunk is broken for that specific client/edge. To tell *which* chunk and
 * *why* (truncated download vs corrupted runtime env-injection vs stale
 * immutable cache) we re-fetch the failing URL and capture hard evidence
 * (status, byte length vs content-length, cache headers, leftover
 * `OVERRIDE_VITE_*` placeholders, and the file's tail).
 */

const BUILD_VERSION =
  __BUILD_VERSION__ || import.meta.env.VITE_APP_VERSION || 'unknown';

// Avoid spamming Sentry / the console when the same chunk fails repeatedly.
const probedUrls = new Set<string>();

// One-time auto-reload recovery: a broken lazy chunk is usually a stale/edge
// artifact, so a single hard reload typically pulls a good copy. Guarded via
// sessionStorage so we never get stuck in a reload loop if the chunk is truly
// broken at origin (in that case we let the error surface instead).
const RELOAD_GUARD_KEY = 'chunkReloadGuardAt';
const RELOAD_GUARD_WINDOW_MS = 30000;
// Small delay so the probe + Sentry event have a chance to flush before reload.
const RELOAD_DELAY_MS = 1500;

const attemptChunkRecovery = (source: string): void => {
  try {
    const now = Date.now();
    const lastReloadAt = Number(
      window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0'
    );

    if (now - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
      console.warn(
        `[chunkLoadDiagnostics] recovery skipped — already reloaded ${Math.round(
          (now - lastReloadAt) / 1000
        )}s ago; surfacing error instead of looping`
      );
      addBreadcrumb(
        'Chunk recovery skipped (reload-loop guard)',
        'chunk-load',
        'warning',
        { source }
      );
      return;
    }

    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    console.warn(
      '[chunkLoadDiagnostics] recovering from chunk load failure with a one-time reload'
    );
    addBreadcrumb('Chunk recovery: one-time reload', 'chunk-load', 'warning', {
      source,
      pagePath: window.location.pathname + window.location.search,
    });

    window.setTimeout(() => {
      window.location.reload();
    }, RELOAD_DELAY_MS);
  } catch {
    // sessionStorage unavailable — do nothing (never risk a reload loop).
  }
};

// Ring buffer of recently-loaded JS/CSS assets, so that when a dynamic import
// fails to PARSE (the error carries no URL) we can still point at the chunks
// the browser just fetched and probe them.
type AssetTiming = {
  name: string;
  encodedBodySize: number;
  decodedBodySize: number;
  transferSize: number;
  responseStatus?: number;
  startTime: number;
};
const recentAssets: AssetTiming[] = [];
const MAX_TRACKED_ASSETS = 60;
const CANDIDATE_WINDOW_MS = 30000;
const MAX_CANDIDATES = 6;

const ASSET_PATH_PATTERN = /\/assets(?:-[^/?#]+)?\/.+\.(?:js|mjs|css)(?:\?|$)/;

const isAssetUrl = (name: string): boolean => ASSET_PATH_PATTERN.test(name);

const trackAsset = (entry: PerformanceResourceTiming): void => {
  recentAssets.push({
    name: entry.name,
    encodedBodySize: entry.encodedBodySize,
    decodedBodySize: entry.decodedBodySize,
    transferSize: entry.transferSize,
    responseStatus: (entry as PerformanceResourceTiming & {
      responseStatus?: number;
    }).responseStatus,
    startTime: entry.startTime,
  });
  if (recentAssets.length > MAX_TRACKED_ASSETS) recentAssets.shift();
};

const recentJsCandidates = (): AssetTiming[] => {
  const now =
    typeof performance !== 'undefined' ? performance.now() : Number.MAX_VALUE;
  const seen = new Set<string>();
  return recentAssets
    .filter(
      (asset) =>
        /\.(?:js|mjs)(?:\?|$)/.test(asset.name) &&
        now - asset.startTime < CANDIDATE_WINDOW_MS
    )
    .sort((a, b) => b.startTime - a.startTime)
    .filter((asset) => {
      if (seen.has(asset.name)) return false;
      seen.add(asset.name);
      return true;
    })
    .slice(0, MAX_CANDIDATES);
};

const CHUNK_ERROR_PATTERNS = [
  'Unexpected end of input',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Unable to preload CSS',
  'Importing a module script failed',
  'expected expression, got',
  "Unexpected token '<'", // chunk URL resolved to an HTML (404/SSR) page
];

const looksLikeAssetUrl = (value: string): boolean =>
  ASSET_PATH_PATTERN.test(value);

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;

  if (error instanceof SyntaxError) return true;

  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error);

  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

/** Best-effort extraction of an /assets[-build]/*.js|css URL from an error / message. */
export const extractChunkUrl = (
  error: unknown,
  ...hints: (string | undefined)[]
): string | undefined => {
  const haystacks: string[] = [];

  for (const hint of hints) {
    if (hint) haystacks.push(hint);
  }

  if (error instanceof Error) {
    haystacks.push(error.message);
    if (error.stack) haystacks.push(error.stack);
  } else if (typeof error === 'string') {
    haystacks.push(error);
  }

  for (const haystack of haystacks) {
    const match = haystack.match(
      /https?:\/\/[^\s"')]+\/assets(?:-[^/?#\s"')]+)?\/[^\s"')]+/
    );
    if (match) return match[0];

    const relative = haystack.match(
      /\/assets(?:-[^/?#\s"')]+)?\/[^\s"')]+\.(?:js|mjs|css)/
    );
    if (relative) return new URL(relative[0], window.location.origin).href;
  }

  return undefined;
};

/**
 * Re-download the failing chunk and capture forensic details. This is the
 * single most useful signal post-deploy: it tells us whether the bytes the
 * browser received are actually broken (truncated / placeholders / HTML)
 * rather than a transient runtime hiccup.
 */
export const probeChunk = async (
  url: string,
  context: Record<string, unknown> = {}
): Promise<void> => {
  if (probedUrls.has(url)) return;
  probedUrls.add(url);

  const diagnostics: Record<string, unknown> = {
    chunkUrl: url,
    buildVersion: BUILD_VERSION,
    pagePath: window.location.pathname + window.location.search,
    ...context,
  };

  const inspect = (label: string, response: Response, body: string) => {
    const contentLengthHeader = response.headers.get('content-length');
    const leftoverPlaceholders = body.match(/OVERRIDE_VITE_[A-Z0-9_]+/g);
    return {
      [`${label}HttpStatus`]: response.status,
      [`${label}CfCacheStatus`]: response.headers.get('cf-cache-status'),
      [`${label}CacheControl`]: response.headers.get('cache-control'),
      [`${label}Age`]: response.headers.get('age'),
      [`${label}ContentType`]: response.headers.get('content-type'),
      [`${label}ContentLengthHeader`]: contentLengthHeader,
      [`${label}ByteLength`]: new Blob([body]).size,
      [`${label}Truncated`]:
        contentLengthHeader != null &&
        Number(contentLengthHeader) !== new Blob([body]).size,
      [`${label}LooksLikeHtml`]: /^\s*<(?:!doctype|html)/i.test(body),
      [`${label}LeftoverPlaceholders`]: leftoverPlaceholders
        ? Array.from(new Set(leftoverPlaceholders))
        : null,
      [`${label}Tail`]: body.slice(-120),
    };
  };

  try {
    // What the browser actually used (possibly a stale/truncated cached copy)…
    const cachedResponse = await fetch(url, { cache: 'force-cache' });
    const cachedBody = await cachedResponse.text();
    // …vs a guaranteed-fresh copy straight from origin.
    const freshResponse = await fetch(url, { cache: 'reload' });
    const freshBody = await freshResponse.text();

    const cachedLen = new Blob([cachedBody]).size;
    const freshLen = new Blob([freshBody]).size;

    Object.assign(
      diagnostics,
      inspect('cached', cachedResponse, cachedBody),
      inspect('fresh', freshResponse, freshBody),
      {
        // The decisive signal for the immutable-cache hypothesis: the bytes the
        // browser cached differ from what origin currently serves.
        cacheVsOriginMismatch: cachedLen !== freshLen,
        cachedVsResourceTimingMismatch:
          typeof context.cachedDecodedBodySize === 'number' &&
          context.cachedDecodedBodySize > 0 &&
          context.cachedDecodedBodySize !== cachedLen,
      }
    );
  } catch (probeError) {
    diagnostics.probeFailed =
      probeError instanceof Error ? probeError.message : String(probeError);
  }

  console.error('[chunkLoadDiagnostics] Lazy chunk failed to load', diagnostics);
  addBreadcrumb(
    `Chunk load failure: ${url}`,
    'chunk-load',
    'error',
    diagnostics
  );

  const reported = new Error(`Chunk load failure: ${url}`);
  reported.name = 'ChunkLoadDiagnostic';
  reportError(reported, { type: 'chunkLoadError', ...diagnostics });
};

/**
 * Central entry point: given any error we suspect is a chunk-load failure,
 * confirm + probe the offending URL.
 */
export const reportChunkLoadFailure = (
  error: unknown,
  source: string,
  options: { urlHint?: string; recover?: boolean } = {}
): void => {
  const { urlHint, recover = false } = options;
  const url = extractChunkUrl(error, urlHint);

  console.error(
    `[chunkLoadDiagnostics] (${source})`,
    error instanceof Error ? `${error.name}: ${error.message}` : error,
    url ? `-> ${url}` : '(chunk url not found in error)'
  );

  // Attempt one-time reload recovery (guarded against loops). Diagnostics below
  // still run; on a genuinely broken-at-origin chunk the guard stops the loop
  // and the full report fires on the next occurrence.
  if (recover) {
    attemptChunkRecovery(source);
  }

  if (url) {
    void probeChunk(url, { source, errorName: (error as Error)?.name });
    return;
  }

  // A parse failure of a dynamically-imported module (SyntaxError: Unexpected
  // end of input) gives no URL. Fall back to the chunks the browser just
  // fetched and probe each of them — the broken one reveals itself.
  const candidates = recentJsCandidates();

  console.error(
    `[chunkLoadDiagnostics] (${source}) no URL in error; probing ${candidates.length} recent JS chunk(s):`,
    candidates.map((candidate) => candidate.name)
  );

  addBreadcrumb(
    `Chunk load failure with no URL (${source}) — probing recent chunks`,
    'chunk-load',
    'error',
    {
      message:
        error instanceof Error
          ? `${error.name}: ${error.message}`
          : String(error),
      pagePath: window.location.pathname + window.location.search,
      buildVersion: BUILD_VERSION,
      candidates: candidates.map((candidate) => ({
        url: candidate.name,
        encodedBodySize: candidate.encodedBodySize,
        decodedBodySize: candidate.decodedBodySize,
        transferSize: candidate.transferSize,
        responseStatus: candidate.responseStatus,
      })),
    }
  );

  for (const candidate of candidates) {
    void probeChunk(candidate.name, {
      source: `${source}:candidate`,
      errorName: (error as Error)?.name,
      cachedDecodedBodySize: candidate.decodedBodySize,
      cachedTransferSize: candidate.transferSize,
      resourceTimingStatus: candidate.responseStatus,
    });
  }
};

/** Track JS/CSS asset loads so failing chunks can be identified by recency. */
const setupResourceObserver = (): void => {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    for (const entry of performance.getEntriesByType(
      'resource'
    ) as PerformanceResourceTiming[]) {
      if (isAssetUrl(entry.name)) trackAsset(entry);
    }

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
        if (entry.entryType === 'resource' && isAssetUrl(entry.name)) {
          trackAsset(entry);
        }
      }
    });
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    // Resource timing not available — candidate probing will just be empty.
  }
};

/**
 * Attach listeners for the two ways a broken lazy chunk surfaces:
 *  1. Vite's `vite:preloadError` (dynamic import / modulepreload failed).
 *  2. A module `<script>` that downloads but fails to evaluate -> a window
 *     `error` event whose `filename` is the chunk URL (e.g. SyntaxError).
 */
export const setupChunkLoadDiagnostics = (): void => {
  if (typeof window === 'undefined') return;

  setupResourceObserver();

  // 1. Vite dynamic-import / preload failures.
  // `vite:preloadError` is not in the DOM lib's WindowEventMap.
  window.addEventListener('vite:preloadError' as keyof WindowEventMap, (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    reportChunkLoadFailure(payload ?? event, 'vite:preloadError', {
      recover: true,
    });
  });

  // 2. Module script evaluation/parse failures (SyntaxError et al).
  window.addEventListener(
    'error',
    (event) => {
      const filename = (event as ErrorEvent).filename;
      const error = (event as ErrorEvent).error;
      const message = (event as ErrorEvent).message;

      const fromJsAsset = filename
        ? /\/assets(?:-[^/?#]+)?\/.+\.(?:js|mjs)(?:\?|$)/.test(filename)
        : false;
      const fromAsset = filename ? looksLikeAssetUrl(filename) : false;

      if (fromAsset || isChunkLoadError(error ?? message)) {
        reportChunkLoadFailure(error ?? message, 'window.error', {
          urlHint: filename,
          // Only auto-reload for JS chunk failures, not e.g. a broken CSS/image.
          recover: fromJsAsset || isChunkLoadError(error ?? message),
        });
      }
    },
    true
  );
};
